import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'
import { TaskRow } from '../components/tasks/TaskRow.jsx'
import {
  DELETE_TASK,
  UPDATE_TASK,
} from '../components/tasks/graphql.js'
import {
  nextAssignee,
  nextPriority,
  nextStatus,
} from '../components/tasks/utils/cycling.js'
import { EmptyState, Button, PageContainer } from '../ui'
import { useTeam } from '../hooks/useTeam.js'
import { PairSubscriber } from './tasks/PairSubscriber.jsx'
import { TasksBulkActionBar } from './tasks/TasksBulkActionBar.jsx'
import { TasksConfirmDialog } from '../components/tasks/TasksConfirmDialog.jsx'
import { TasksFilters, countActiveFilters } from './tasks/TasksFilters.jsx'
import { TasksPageHeader } from './tasks/TasksPageHeader.jsx'
import { TasksTabs } from './tasks/TasksTabs.jsx'
import { usePairs } from './tasks/hooks/usePairs.js'
import { useTasksPage } from './tasks/hooks/useTasksPage.js'

const FILTERS_PANEL_ID = 'tasks-filters-panel'

/**
 * The dedicated /tasks page (state-routed in the app shell, no React
 * Router). Cross-pair view powered by `tasksAssignedToMe`.
 */
export function TasksPage({ me, currentUserId, onCreateClicked }) {
  const { reports } = useTeam({ enabled: !!me?.userId })
  const pairs = usePairs(me, reports ?? [])
  const isManager = pairs.some((p) => p.role === 'manager')

  const page = useTasksPage({ currentUserId })
  const {
    tab,
    setTab,
    tasks,
    openCount,
    pastCount,
    overdueCount,
    loading,
    error,
    hasMore,
    loadMore,
    filters,
    setFilters,
    selectedIds,
    toggleSelected,
    clearSelection,
    snapshotState,
    restoreSnapshot,
    applyOptimisticUpdate,
    applyOptimisticRemove,
    handleCreated,
    handleUpdated,
    handleDeleted,
    refetch,
  } = page

  const selectedTasks = useMemo(
    () => tasks.filter((t) => selectedIds.includes(t.taskId)),
    [tasks, selectedIds],
  )

  const [filtersOpen, setFiltersOpen] = useState(false)
  const toggleFilters = useCallback(() => setFiltersOpen((o) => !o), [])
  const activeFilterCount = countActiveFilters(filters)

  // ── Inline status (single-line summary for bulk ops + load errors) ────
  const [statusMessage, setStatusMessage] = useState(null)
  const statusTimerRef = useRef(null)
  const showStatus = useCallback((text) => {
    setStatusMessage(text)
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current)
    statusTimerRef.current = setTimeout(() => setStatusMessage(null), 5000)
  }, [])
  useEffect(() => () => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current)
  }, [])

  // ── Bulk action runner ────────────────────────────────────────────────
  const runBulk = useCallback(
    async (handler, label) => {
      if (selectedTasks.length === 0) return
      const snapshot = snapshotState()
      try {
        const results = await Promise.allSettled(selectedTasks.map(handler))
        const failed = results.filter((r) => r.status === 'rejected').length
        const success = results.length - failed
        if (failed > 0) {
          // Restore the prior state for the whole bulk so failed items reappear.
          restoreSnapshot(snapshot)
          showStatus(`${label}: ${success} of ${results.length} updated · ${failed} failed`)
        } else {
          showStatus(`${label}: ${success} of ${results.length} updated`)
        }
      } catch (err) {
        restoreSnapshot(snapshot)
        showStatus(`${label}: failed — please retry`)
      }
      clearSelection()
      refetch()
    },
    [selectedTasks, snapshotState, restoreSnapshot, clearSelection, showStatus, refetch],
  )

  const handleMarkDone = useCallback(
    () =>
      runBulk(async (task) => {
        applyOptimisticUpdate(task.taskId, { status: 'DONE' })
        await appsyncQuery(UPDATE_TASK, {
          input: { taskId: task.taskId, reportId: task.reportId, status: 'DONE' },
        })
      }, 'Mark done'),
    [runBulk, applyOptimisticUpdate],
  )

  const handleMarkCancelled = useCallback(
    () =>
      runBulk(async (task) => {
        applyOptimisticUpdate(task.taskId, { status: 'CANCELLED' })
        await appsyncQuery(UPDATE_TASK, {
          input: { taskId: task.taskId, reportId: task.reportId, status: 'CANCELLED' },
        })
      }, 'Mark cancelled'),
    [runBulk, applyOptimisticUpdate],
  )

  const handleReassign = useCallback(
    (assigneeId) =>
      runBulk(async (task) => {
        applyOptimisticUpdate(task.taskId, { assignee: assigneeId })
        await appsyncQuery(UPDATE_TASK, {
          input: { taskId: task.taskId, reportId: task.reportId, assignee: assigneeId },
        })
      }, 'Reassign'),
    [runBulk, applyOptimisticUpdate],
  )

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const handleDelete = useCallback(() => setConfirmDeleteOpen(true), [])
  const handleConfirmDelete = useCallback(async () => {
    setConfirmDeleteOpen(false)
    await runBulk(async (task) => {
      applyOptimisticRemove(task.taskId)
      await appsyncQuery(DELETE_TASK, { taskId: task.taskId, reportId: task.reportId })
    }, 'Delete')
  }, [runBulk, applyOptimisticRemove])

  // Surface fetch errors via the inline status.
  useEffect(() => {
    if (error) showStatus(`Couldn't load tasks: ${error.message ?? 'please retry'}`)
  }, [error, showStatus])

  // ── Empty state copy ──────────────────────────────────────────────────
  let emptyMessage = null
  if (tasks.length === 0 && !loading) {
    if (activeFilterCount > 0) {
      emptyMessage = 'No tasks match these filters.'
    } else if (tab === 'open') {
      emptyMessage = "You're all caught up. Nice work."
    } else {
      emptyMessage = 'No completed or cancelled tasks yet.'
    }
  }

  // Per-row inline mutations. We can't use the phase 2 <TaskList> wrapper
  // here because it fetches via tasksForPair (single pair) — the page is
  // cross-pair. Instead we render <TaskRow> directly and route mutations
  // through the page's optimistic state, with each row carrying its own
  // pair context.
  const inlineUpdate = useCallback(
    async (task, patch) => {
      const snapshot = snapshotState()
      applyOptimisticUpdate(task.taskId, { ...patch, updatedAt: new Date().toISOString() })
      try {
        await appsyncQuery(UPDATE_TASK, {
          input: { taskId: task.taskId, reportId: task.reportId, ...patch },
        })
      } catch {
        restoreSnapshot(snapshot)
        showStatus("Couldn't save change — please retry")
      }
    },
    [snapshotState, applyOptimisticUpdate, restoreSnapshot, showStatus],
  )

  const inlineDelete = useCallback(
    async (task) => {
      const snapshot = snapshotState()
      applyOptimisticRemove(task.taskId)
      try {
        await appsyncQuery(DELETE_TASK, { taskId: task.taskId, reportId: task.reportId })
      } catch {
        restoreSnapshot(snapshot)
        showStatus("Couldn't delete — please retry")
      }
    },
    [snapshotState, applyOptimisticRemove, restoreSnapshot, showStatus],
  )

  return (
    <div style={styles.page}>
      {/* Subscribe across every pair the user belongs to */}
      {pairs.map((p) => (
        <PairSubscriber
          key={p.pairId}
          reportId={p.reportId}
          managerId={p.managerId}
          enabled
          onTaskCreated={handleCreated}
          onTaskUpdated={handleUpdated}
          onTaskDeleted={handleDeleted}
        />
      ))}

      <PageContainer>
        <TasksPageHeader
          openCount={openCount}
          overdueCount={overdueCount}
          onCreateClicked={onCreateClicked}
          filtersOpen={filtersOpen}
          filterActiveCount={activeFilterCount}
          filtersPanelId={FILTERS_PANEL_ID}
          onToggleFilters={toggleFilters}
        />

        <TasksFilters
          tab={tab}
          filters={filters}
          onFiltersChange={setFilters}
          isManager={isManager}
          open={filtersOpen}
          panelId={FILTERS_PANEL_ID}
        />

        <TasksTabs
          tab={tab}
          onTabChange={setTab}
          openCount={openCount}
          pastCount={pastCount}
        />

        <div style={styles.main}>
          <TasksBulkActionBar
            selectedTasks={selectedTasks}
            pairs={pairs}
            onMarkDone={handleMarkDone}
            onMarkCancelled={handleMarkCancelled}
            onReassign={handleReassign}
            onDelete={handleDelete}
            onClear={clearSelection}
          />
          {statusMessage && (
            <div role="status" style={styles.statusBar}>
              {statusMessage}
            </div>
          )}

          {loading ? (
            <div style={styles.empty} role="status">Loading tasks…</div>
          ) : emptyMessage ? (
            <EmptyState body={emptyMessage} />
          ) : (
            <>
              <div role="list" data-testid="tasks-page-list" style={styles.list}>
                {tasks.map((task) => {
                  const pair = pairs.find(
                    (p) => p.reportId === task.reportId && p.managerId === task.managerId,
                  )
                  return (
                    <TaskRow
                      key={task.taskId}
                      task={task}
                      reportId={task.reportId}
                      managerId={task.managerId}
                      reportName={pair?.reportName ?? 'Report'}
                      managerName={pair?.managerName ?? 'Manager'}
                      currentUserId={currentUserId}
                      selectionMode
                      selected={selectedIds.includes(task.taskId)}
                      onToggleSelected={() => toggleSelected(task.taskId)}
                      onCycleStatus={() => inlineUpdate(task, { status: nextStatus(task.status) })}
                      onCyclePriority={() => inlineUpdate(task, { priority: nextPriority(task.priority) })}
                      onCycleAssignee={() =>
                        inlineUpdate(task, {
                          assignee: nextAssignee(task.assignee, task.reportId, task.managerId),
                        })
                      }
                      onSetDueDate={(dueDate) => inlineUpdate(task, { dueDate })}
                      onUpdateTitle={(title) => inlineUpdate(task, { title })}
                      onDelete={() => inlineDelete(task)}
                    />
                  )
                })}
              </div>
              {hasMore && (
                <div style={{ alignSelf: 'center' }}>
                  <Button variant="secondary" onClick={loadMore} aria-label="Load more tasks">
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </PageContainer>

      <TasksConfirmDialog
        open={confirmDeleteOpen}
        title={`Delete ${selectedTasks.length} task${selectedTasks.length === 1 ? '' : 's'}?`}
        message="This soft-deletes the selected tasks. They can be restored from the database for the configured retention window."
        confirmLabel="Delete"
        danger
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '24px 0',
    flex: 1,
    minHeight: 0,
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  statusBar: {
    fontSize: 12,
    padding: '6px 10px',
    borderRadius: 6,
    background: '#F5F8FB',
    border: '1px solid #d6e2ee',
    color: 'var(--text-primary)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--surface, #fff)',
    border: '1px solid var(--border, #e8e8e6)',
    borderRadius: 8,
    padding: '8px 14px',
  },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: 'var(--text-secondary, #888)',
    fontSize: 13,
  },
}
